import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { ProfileDTO2, ProfileDTO2Role, Role, UpdateProfileDTO2, UpdateProfileDTO2Region } from '../../api/api';
import { CloseIcon } from '../../assets/icons/CloseIcon';
import Button from '../../components/button/Button';
import DepartmentDropdown from '../../components/department-dropdown/DepartmentDropdown';
import DialogView from '../../components/dialog-view/DialogView';
import IconButton from '../../components/icon-button/IconButton';
import LoadingOverlay from '../../components/loading-overlay/LoadingOverlay';
import TextField from '../../components/text-field/TextField';
import Typography from '../../components/typography/Typography';
import { useNavigate } from '../../hooks/useNavigate';
import NotificationService from '../../services/NotificationService';
import UserService from '../../services/UserService';
import { Row, Section, SectionContent, SectionHeader } from '../../styling/FormStyling';
import { log } from '../../utils/logging/log';
import useUserAccess from '../../hooks/useUserAccess';
import TestingService, { allowedTestingEnviroments } from '../../services/TestingService';
import DropPointsDropdown from '../../components/hi/DropPointsDropdown';
import RolesDropdown from '../../components/hi/RolesDropdown';
import Select, { MenuItem } from '../../components/select/Select';
import { FormControl } from '@mui/material';
import Alert from '../../components/alert/Alert';
import { useDispatch } from 'react-redux';
import { setUserProfileState } from '../../stateManagement/reducers/userProfileReducer';
import Checkbox from '../../components/checkbox/Checkbox';

type Errors = { [key in keyof UpdateProfileDTO2]: boolean | undefined };

const ProfileView = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const { officeUserGroupAllowed } = useUserAccess();

  const [profileValues, setProfileValues] = useState<ProfileDTO2>();
  const [role, setRole] = useState<ProfileDTO2Role | undefined>();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  useEffect(() => {
    setIsLoading(true);
    UserService.getUser()
      .then((res) => {
        setProfileValues({
          name: res.name,
          phone: res.phone,
          department: res.department,
          defaultWarehouseId: res.defaultDropPoint?.warehouseId,
          defaultDropPoint: res.defaultDropPoint,
          favoriteDropPoint: res.favoriteDropPoint,
          role: res.role,
          isAutomaticNotificationsEnabled: res.isAutomaticNotificationsEnabled
        });
        setRole(res.role);
      })
      .catch((error) => {
        NotificationService.error(`Kunne ikke hente bruger data: ${error}`);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const formValidated = () => {
    setErrors({});
    let tmpErrors: Errors = {};

    if (!profileValues?.name) tmpErrors.name = true;
    if (!profileValues?.department?.departmentId && role !== ProfileDTO2Role.Technician) tmpErrors.departmentId = true;

    if (Object.keys(tmpErrors).length) {
      setErrors(tmpErrors);
      return false;
    }
    return true;
  };

  const submit = () => {
    if (!formValidated()) {
      NotificationService.error(`Formularen er ikke korrekt udfyldt.`);
      return;
    }

    setIsLoading(true);

    const body: UpdateProfileDTO2 = {
      departmentId: profileValues?.department?.departmentId,
      favoriteWarehouseId: profileValues?.favoriteDropPoint?.warehouseId,
      name: profileValues?.name,
      phone: profileValues?.phone,
      region: profileValues?.region as unknown as UpdateProfileDTO2Region,
      isAutomaticNotificationsEnabled: profileValues?.isAutomaticNotificationsEnabled
    };

    UserService.updateProfile(body)
      .then(() => {
        if (!profileValues) return;
        dispatch(setUserProfileState(profileValues));
        NotificationService.success('Profil opdateret');
        navigate(-1);
      })
      .catch((error) => {
        NotificationService.error(`Kunne ikke opdatere profil: ${error}`);
        log(error);
      })
      .finally(() => {
        setIsLoading(false);
        if (allowedTestingEnviroments && profileValues?.role !== role) {
          TestingService.changeRole(role as unknown as Role).then(() => {
            window.location.reload();
          });
        }
      });
  };

  const handleChange = (key: keyof ProfileDTO2, value: any) => {
    setProfileValues((prev) => {
      return {
        ...prev,
        [key]: value
      };
    });
  };

  return (
    <DialogView handleClose={() => navigate(-1)}>
      <Container>
        {isLoading && <LoadingOverlay />}
        <Header>
          <Typography variant="h3">Min profil</Typography>
          <IconButton variant="outlined" onClick={() => navigate(-1)}>
            <CloseIcon size="18px" />
          </IconButton>
        </Header>
        <HR />
        <Section noBorder>
          <SectionHeader>Kontaktoplysninger</SectionHeader>
          <SectionContent>
            <Row>
              <TextField
                required
                label="Fulde navn"
                fullWidth
                error={errors?.name}
                onChange={(e) => handleChange('name', e.target.value)}
                value={profileValues?.name || ''}
              />
              <TextField
                label="Telefonnummer"
                fullWidth
                error={errors?.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                value={profileValues?.phone || ''}
                inputProps={{ maxLength: 20 }}
              />
            </Row>
            <Row>
              {officeUserGroupAllowed() && (
                <DepartmentDropdown
                  label="Afdeling"
                  value={profileValues?.department?.departmentId}
                  selectDepartment={(department) => handleChange('department', department)}
                  fullWidth
                  error={errors?.departmentId}
                />
              )}
            </Row>
            <Row>
              <FormControl fullWidth>
                <Select
                  label="Standard dropsted"
                  disabled
                  value={'1'}
                  sx={{
                    appearance: 'none',
                    '& .MuiSelect-icon': { display: 'none' }
                  }}
                >
                  <MenuItem value={'1'}>{profileValues?.defaultDropPoint?.name ?? 'Intet standard dropsted'}</MenuItem>
                </Select>
              </FormControl>
              <DropPointsDropdown
                label="Favorit dropsted"
                warehouseId={profileValues?.favoriteDropPoint?.warehouseId}
                onChange={(dropPoint) => handleChange('favoriteDropPoint', dropPoint)}
                error={errors.favoriteWarehouseId}
              />
            </Row>
            <Row>
              {allowedTestingEnviroments && (
                <RolesDropdown role={role} onChange={(role) => setRole(role as ProfileDTO2Role)} />
              )}
              <CheckboxContainer>
                <Checkbox
                  label="Automatiske notifikationer ved opgaveoprettelse"
                  checked={profileValues?.isAutomaticNotificationsEnabled}
                  onChange={(e) => handleChange('isAutomaticNotificationsEnabled', e.target.checked)}
                />
              </CheckboxContainer>
            </Row>
          </SectionContent>
        </Section>
        <HR />
        <FormFooter>
          {role !== profileValues?.role && (
            <Alert margin="0px 10px" severity="warning" height="42px">
              Ændring i rolle vil medføre en opdatering af denne side.
            </Alert>
          )}
          <Button onClick={submit}>Gem profil</Button>
        </FormFooter>
      </Container>
    </DialogView>
  );
};

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
  text-align: start;
  padding: 32px 24px 24px 32px;

  background-color: white;
  width: 800px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const HR = styled.hr`
  margin: 0 -24px 0 -32px;
  height: 1px;
  border: none;
  background-color: ${(props) => props.theme.palette.grey.black10};
`;

const CheckboxContainer = styled.div`
  width: 100%;
`;

export default ProfileView;
